import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/locale";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  ShoppingBasket, Wrench, Sofa, Receipt, Leaf, Shield,
  Flame, ListChecks, Store, Car, PawPrint, Bell,
  ArrowUpRight, AlertTriangle, Sparkles, BellRing,
  Package, Timer,
} from "lucide-react";
import { toast } from "sonner";
import BudgetTile from "@/components/BudgetTile";
import InstallPWA from "@/components/InstallPWA";
import TrialRingCard from "@/components/TrialRingCard";

const TILES = [
  { to: "/grocery",     label: "Grocery list",   tone: "text-hearth-clay",      icon: ShoppingBasket, key: "grocery_upcoming",   suffix: "to buy" },
  { to: "/maintenance", label: "Open repairs",   tone: "text-hearth-coral",     icon: Wrench,         key: "open_maintenance",   suffix: "items" },
  { to: "/bills",       label: "Unpaid bills",   tone: "text-hearth-coral",     icon: Flame,          key: "unpaid_bills",       suffix: "due" },
  { to: "/chores",      label: "Open chores",    tone: "text-hearth-clay",      icon: ListChecks,     key: "open_chores",        suffix: "to do" },
  { to: "/plants",      label: "Plants",         tone: "text-hearth-sageDark",  icon: Leaf,           key: "plants",             suffix: "in care" },
  { to: "/wishlist",    label: "Wishlist",       tone: "text-[#7A5320]",        icon: Sofa,           key: "wishlist",           suffix: "ideas" },
  { to: "/warranties",  label: "Warranties",     tone: "text-hearth-slate",     icon: Receipt,        key: "warranties",         suffix: "tracked" },
  { to: "/insurance",   label: "Insurance",      tone: "text-hearth-ink",       icon: Shield,         key: "insurance",          suffix: "policies" },
  { to: "/vehicles",    label: "Vehicles",       tone: "text-hearth-slate",     icon: Car,            key: "vehicles",           suffix: "garaged" },
  { to: "/pets",        label: "Pets",           tone: "text-hearth-clay",      icon: PawPrint,       key: "pets",               suffix: "members" },
  { to: "/vendors",     label: "Vendors",        tone: "text-hearth-sageDark",  icon: Store,          key: "vendors",            suffix: "shops" },
];

// add a virtual tile for expenses summary (handled separately, just nav)

const KIND_ICON = {
  bill: Flame, insurance: Shield, warranty: Receipt, maintenance: Wrench,
  vehicle: Car, pet: PawPrint, chore: ListChecks,
};
const SEV_DOT = { high: "bg-hearth-coral", medium: "bg-hearth-amber", low: "bg-hearth-sage" };

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [household, setHousehold] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [budget, setBudget] = useState(null);
  const [digest, setDigest] = useState(null);  // v27.18 — daily digest merged into Up next card

  const load = useCallback(async () => {
    try {
      const [d, h, r, b] = await Promise.all([
        api.get("/dashboard"),
        api.get("/household/me"),
        api.get("/reminders", { params: { horizon_days: 30 } }),
        api.get("/budget/snapshot"),
      ]);
      setData(d.data);
      setHousehold(h.data);
      setReminders(r.data.items.slice(0, 6));
      setBudget(b.data);
      // Digest is owner-only and non-critical — fail silently for members.
      try {
        const dig = await api.post("/push/digest-preview");
        setDigest(dig.data);
      } catch (_e) { setDigest(null); }
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // In-app browser notifications: on Dashboard load, notify user about overdue or due-today items.
  useEffect(() => {
    if (!("Notification" in window) || reminders.length === 0) return;
    if (Notification.permission !== "granted") return;
    const urgent = reminders.filter((r) => r.days_until <= 0);
    if (urgent.length === 0) return;
    const seenKey = `hh-notified-${new Date().toISOString().slice(0, 10)}`;
    if (localStorage.getItem(seenKey)) return; // once per day
    localStorage.setItem(seenKey, "1");
    const title = urgent.length === 1
      ? "HearthHouse — 1 item needs attention"
      : `HearthHouse — ${urgent.length} items need attention`;
    const body = urgent.slice(0, 3).map((u) => `${u.title} (${u.days_until < 0 ? Math.abs(u.days_until) + "d overdue" : "today"})`).join(" · ");
    try { new Notification(title, { body, icon: "/icon-192.png", tag: "hh-daily" }); }
    catch (err) { console.warn("Notification failed:", err?.message); }
  }, [reminders]);

  const requestNotifPermission = async () => {
    if (!("Notification" in window)) { toast.error("Notifications not supported in this browser"); return; }
    if (Notification.permission === "granted") { toast.info("Notifications already on"); return; }
    if (Notification.permission === "denied") { toast.error("Enable notifications in your browser settings"); return; }
    const res = await Notification.requestPermission();
    if (res === "granted") toast.success("Notifications enabled — you'll see overdue items the next time you open HearthHouse");
  };

  if (!data) return <div className="text-hearth-mute font-display italic text-2xl">A moment…</div>;
  const c = data.counts;
  const attention = (c.unpaid_bills || 0) + (c.open_chores || 0) + (data.urgent_maintenance?.length || 0)
                    + reminders.filter((r) => r.severity === "high").length;

  return (
    <div className="space-y-10">
      <InstallPWA />
      <TrialRingCard />
      {/* Hero: editorial split */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 fade-up">
          <p className="caption">{household?.name} · {(household?.members || []).length} {(household?.members || []).length === 1 ? "person" : "people"}</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-hearth-ink mt-3 leading-[0.95] tracking-tight">
            The house, <span className="italic text-hearth-clay">today</span>.
          </h1>
          <p className="text-hearth-mute mt-5 max-w-xl leading-relaxed">
            {attention > 0
              ? <>You have <span className="text-hearth-ink font-semibold num">{attention}</span> thing{attention === 1 ? "" : "s"} asking for attention. Everything else is in order.</>
              : <>Everything&apos;s in order — no urgent items, no overdue tasks.</>}
          </p>
          <div className="mt-7 flex flex-wrap gap-2">
            {(household?.members || []).slice(0, 5).map((m) => (
              <div key={m.id} className="flex items-center gap-2 bg-hearth-paper border border-hearth-line rounded-full pl-1 pr-3 py-1">
                <div className="w-6 h-6 rounded-full bg-hearth-slate text-white grid place-items-center text-[10px] font-medium">
                  {m.name?.[0]?.toUpperCase()}
                </div>
                <span className="text-sm text-hearth-ink">{m.name}</span>
              </div>
            ))}
            <Link to="/household" data-testid="dashboard-invite-link"
                  className="pill-btn bg-hearth-ink text-white hover:bg-hearth-slateDark inline-flex items-center gap-2">
              Invite household <ArrowUpRight size={13} />
            </Link>
          </div>
        </div>

        <aside className="lg:col-span-5 warm-card p-6 fade-up" data-testid="dashboard-up-next">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 caption"><Bell size={12} /> Up next</div>
            <Link to="/reminders" data-testid="dashboard-all-reminders" className="text-xs text-hearth-clay hover:underline">View all →</Link>
          </div>

          {/* v27.18 — digest strip: gentle summary of today's pantry+bills work.
              Owner-only endpoint, so members see reminders only. */}
          {digest && digest.has_content && (
            <div data-testid="dashboard-digest-strip" className="mb-3 rounded-2xl bg-hearth-stone/60 border border-hearth-line/60 px-4 py-3">
              <p className="caption text-hearth-clay mb-2">Today at a glance</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-sm text-hearth-ink">
                {digest.pantry?.length > 0 && (
                  <Link
                    to="/use-soon"
                    data-testid="dashboard-digest-pantry"
                    className="inline-flex items-center gap-1.5 hover:text-hearth-clay transition"
                  >
                    <Package size={13} className="text-hearth-clay" />
                    <span>{digest.pantry.length} pantry item{digest.pantry.length !== 1 ? "s" : ""} to use</span>
                  </Link>
                )}
                {digest.bills_overdue?.length > 0 && (
                  <Link
                    to="/bills"
                    data-testid="dashboard-digest-bills-overdue"
                    className="inline-flex items-center gap-1.5 text-hearth-coral hover:text-hearth-coral/80 transition"
                  >
                    <AlertTriangle size={13} />
                    <span>{digest.bills_overdue.length} bill{digest.bills_overdue.length !== 1 ? "s" : ""} overdue</span>
                  </Link>
                )}
                {digest.bills_due_today?.length > 0 && (
                  <Link
                    to="/bills"
                    data-testid="dashboard-digest-bills-today"
                    className="inline-flex items-center gap-1.5 hover:text-hearth-clay transition"
                  >
                    <Flame size={13} className="text-hearth-clay" />
                    <span>{digest.bills_due_today.length} bill{digest.bills_due_today.length !== 1 ? "s" : ""} due today</span>
                  </Link>
                )}
                {digest.bills_upcoming?.length > 0 && (
                  <Link
                    to="/bills"
                    data-testid="dashboard-digest-bills-upcoming"
                    className="inline-flex items-center gap-1.5 text-hearth-mute hover:text-hearth-ink transition"
                  >
                    <Timer size={13} />
                    <span>{digest.bills_upcoming.length} coming up</span>
                  </Link>
                )}
              </div>
            </div>
          )}

          {reminders.length === 0 ? (
            <div className="py-8 text-center" data-testid="dashboard-reminders-empty">
              <Sparkles className="mx-auto text-hearth-sage/60" size={20} />
              <p className="text-sm text-hearth-mute mt-2">
                {digest?.has_content
                  ? "No reminders in the next 30 days — you're on top of things."
                  : "All clear for the next 30 days."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-hearth-line/70">
              {reminders.map((r) => {
                const Icon = KIND_ICON[r.kind] || Bell;
                const key = `${r.kind}-${r.ref_id}-${r.date}`;
                return (
                  <Link key={key} to={r.link} className="flex items-center gap-3 py-2.5 group">
                    <span className={`w-1.5 h-1.5 rounded-full ${SEV_DOT[r.severity]}`} />
                    <Icon size={14} className="text-hearth-mute" />
                    <span className="text-sm text-hearth-ink flex-1 truncate group-hover:text-hearth-clay transition">{r.title}</span>
                    <span className="text-xs font-mono text-hearth-mute">
                      {r.days_until < 0 ? `${Math.abs(r.days_until)}d late` : r.days_until === 0 ? "today" : `${r.days_until}d`}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </aside>
      </section>

      {/* KPI grid */}
      <section>
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display text-3xl text-hearth-ink">At a glance</h2>
          <span className="caption">11 modules</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {TILES.map((t, i) => {
            const Icon = t.icon;
            const n = c[t.key] ?? 0;
            return (
              <Link
                to={t.to} key={t.to}
                data-testid={`dashboard-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                style={{ animationDelay: `${i * 35}ms` }}
                className="data-card p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all fade-up group flex flex-col"
              >
                <div className="flex items-start justify-between">
                  <Icon size={16} strokeWidth={1.6} className={t.tone} />
                  <ArrowUpRight size={13} className="text-hearth-line group-hover:text-hearth-clay transition" />
                </div>
                <div className="mt-5 stat-num text-hearth-ink num">{n}</div>
                <div className="mt-1 text-[11px] leading-tight">
                  <div className="text-hearth-ink font-medium">{t.label}</div>
                  <div className="text-hearth-mute">{t.suffix}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Monthly budget snapshot */}
      <BudgetTile />

      {/* Insurance at a glance + Enable notifications */}
      {(budget && (budget.monthly_insurance > 0 || c.insurance > 0)) && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dashboard-insurance-glance">
          <Link to="/insurance" className="warm-card p-6 fade-up hover:-translate-y-0.5 transition">
            <div className="flex items-start justify-between">
              <div>
                <div className="caption inline-flex items-center gap-1.5"><Shield size={12} /> Insurance at a glance</div>
                <div className="stat-num text-hearth-ink num mt-2" data-testid="insurance-monthly-total">{money(budget.monthly_insurance)}</div>
                <p className="text-sm text-hearth-mute mt-1">/ month across <span className="num text-hearth-ink">{c.insurance || 0}</span> {c.insurance === 1 ? "policy" : "policies"}</p>
                <p className="text-xs text-hearth-mute mt-2">Annualized: <span className="font-mono text-hearth-ink">{money(budget.monthly_insurance * 12, { withCents: false })}</span> a year</p>
              </div>
              <ArrowUpRight size={16} className="text-hearth-mute" />
            </div>
          </Link>
          {"Notification" in window && Notification.permission !== "granted" && (
            <div className="warm-card p-6 fade-up border-hearth-clay/25 bg-hearth-clay/5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-hearth-clay/15 text-hearth-clayDark grid place-items-center shrink-0">
                  <BellRing size={17} />
                </div>
                <div className="flex-1">
                  <h4 className="font-display text-xl text-hearth-ink">Turn on notifications</h4>
                  <p className="text-sm text-hearth-mute mt-1">
                    Get a browser alert about overdue bills, vet visits and expiring pantry — the moment you open HearthHouse each morning.
                  </p>
                  <button
                    data-testid="enable-notifications-button"
                    onClick={requestNotifPermission}
                    className="pill-btn bg-hearth-clay hover:bg-hearth-clayDark text-white mt-3 inline-flex items-center gap-1.5"
                  >
                    <BellRing size={13} /> Enable notifications
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}


      {/* Urgent maintenance + bills row */}
      {(data.urgent_maintenance?.length > 0 || data.upcoming_bills?.length > 0) && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {data.urgent_maintenance?.length > 0 && (
            <div className="warm-card p-6 fade-up">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="font-display text-2xl text-hearth-ink">High-priority repairs</h3>
                <Link to="/maintenance" className="text-xs text-hearth-clay hover:underline">Open →</Link>
              </div>
              <div className="divide-y divide-hearth-line/70">
                {data.urgent_maintenance.map((m) => (
                  <div key={m.id} className="py-3 flex items-center gap-3">
                    <AlertTriangle size={15} className="text-hearth-coral shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-hearth-ink truncate">{m.title}</div>
                      <div className="caption mt-0.5">{m.category}</div>
                    </div>
                    {m.due_date && <div className="font-mono text-xs text-hearth-mute">{m.due_date}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {data.upcoming_bills?.length > 0 && (
            <div className="warm-card p-6 fade-up">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="font-display text-2xl text-hearth-ink">Bills coming up</h3>
                <Link to="/bills" className="text-xs text-hearth-clay hover:underline">Open →</Link>
              </div>
              <div className="divide-y divide-hearth-line/70">
                {data.upcoming_bills.map((b) => (
                  <div key={b.id} className="py-3 flex items-center gap-3">
                    <Flame size={15} className="text-hearth-clay shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-hearth-ink truncate">{b.name}</div>
                      <div className="caption mt-0.5">{b.category} · {b.recurrence}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm text-hearth-ink">{money(b.amount)}</div>
                      <div className="text-xs text-hearth-mute font-mono">{b.due_date ? fmtDate(b.due_date) : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
